import React, { useState, useEffect } from 'react';
import { Cookie, Shield, BarChart3, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../i18n/translations';

const CookieBanner = () => {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Small delay for better UX
      setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => setIsAnimating(true), 50);
      }, 1500);
    } else if (consent === 'all') {
      // Load analytics if previously accepted
      loadAnalytics();
    }
  }, []);

  const loadAnalytics = () => {
    // Enable Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted'
      });
    }
  };

  const handleAcceptAll = () => {
    localStorage.setItem('cookie_consent', 'all');
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    loadAnalytics();
    closeWithAnimation();
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem('cookie_consent', 'necessary');
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    // Don't load analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied'
      });
    }
    closeWithAnimation();
  };

  const closeWithAnimation = () => {
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 z-[9998] transition-opacity duration-300 ${
          isDark ? 'bg-black/40' : 'bg-black/20'
        } backdrop-blur-sm ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
      />
      
      {/* Banner */}
      <div 
        className={`fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6 transition-all duration-300 ${
          isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
      >
        <div className="max-w-4xl mx-auto">
          <div className={`backdrop-blur-xl rounded-2xl border shadow-2xl overflow-hidden ${
            isDark 
              ? 'bg-[#0f0f0f]/95 border-white/10 shadow-black/50' 
              : 'bg-white/95 border-gray-200 shadow-gray-300/50'
          }`}>
            {/* Main Content */}
            <div className="p-6 sm:p-8">
              <div className="flex items-start gap-4 mb-6">
                {/* Icon */}
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7BA7C2] to-[#5A8BA6] flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-white" />
                </div>
                
                {/* Text */}
                <div className="flex-1">
                  <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {t('cookies.title')}
                  </h3>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {t('cookies.description')}
                  </p>
                </div>
              </div>

              {/* Details Toggle */}
              <button
                onClick={() => setShowDetails(!showDetails)}
                className={`flex items-center gap-2 text-sm font-medium transition-colors mb-6 ${
                  isDark ? 'text-[#7BA7C2] hover:text-white' : 'text-[#5A8BA6] hover:text-gray-900'
                }`}
              >
                {showDetails ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    {language === 'de' ? 'Details ausblenden' : 'Hide details'}
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    {t('cookies.showDetails')}
                  </>
                )}
              </button>

              {/* Cookie Details */}
              {showDetails && (
                <div className="mb-6 space-y-3 animate-in slide-in-from-top-2 duration-200">
                  {/* Necessary Cookies */}
                  <div className={`flex items-start gap-4 p-4 rounded-xl border ${
                    isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-green-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {language === 'de' ? 'Notwendige Cookies' : 'Necessary Cookies'}
                        </h4>
                        <span className="text-xs text-green-500 bg-green-500/10 px-2 py-0.5 rounded-full">
                          {language === 'de' ? 'Immer aktiv' : 'Always active'}
                        </span>
                      </div>
                      <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                        {language === 'de' 
                          ? 'Diese Cookies sind für den Betrieb der Website erforderlich (z.B. Spam-Schutz, Cookie-Präferenzen).'
                          : 'These cookies are required for the website to function (e.g., spam protection, cookie preferences).'}
                      </p>
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className={`flex items-start gap-4 p-4 rounded-xl border ${
                    isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isDark ? 'bg-[#7BA7C2]/10' : 'bg-[#5A8BA6]/10'
                    }`}>
                      <BarChart3 className={`w-5 h-5 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {language === 'de' ? 'Analyse & Marketing' : 'Analytics & Marketing'}
                        </h4>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          isDark ? 'text-gray-400 bg-white/5' : 'text-gray-500 bg-gray-200'
                        }`}>{language === 'de' ? 'Optional' : 'Optional'}</span>
                      </div>
                      <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                        {language === 'de' 
                          ? 'Google Analytics hilft uns zu verstehen, wie Besucher unsere Website nutzen. Google Ads Conversion-Tracking misst die Wirksamkeit unserer Werbung.'
                          : 'Google Analytics helps us understand how visitors use our website. Google Ads Conversion Tracking measures the effectiveness of our advertising.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleAcceptAll}
                  data-testid="cookie-accept-all"
                  className="flex-1 bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white py-6 text-base font-medium shadow-lg shadow-[#7BA7C2]/20"
                >
                  {t('cookies.acceptAll')}
                </Button>
                <Button
                  onClick={handleAcceptNecessary}
                  variant="outline"
                  className={`flex-1 py-6 text-base font-medium ${
                    isDark 
                      ? 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] text-white' 
                      : 'border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  {t('cookies.acceptNecessary')}
                </Button>
              </div>

              {/* Links */}
              <div className={`mt-4 flex items-center justify-center gap-4 text-xs ${
                isDark ? 'text-gray-500' : 'text-gray-500'
              }`}>
                <a href="/datenschutz" className={`transition-colors ${
                  isDark ? 'hover:text-[#7BA7C2]' : 'hover:text-[#5A8BA6]'
                }`}>
                  {t('cookies.privacyPolicy')}
                </a>
                <span>•</span>
                <a href="/impressum" className={`transition-colors ${
                  isDark ? 'hover:text-[#7BA7C2]' : 'hover:text-[#5A8BA6]'
                }`}>
                  {t('cookies.imprint')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
